/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectLegalRequirementCard.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid, Paper, Typography, Radio, IconButton } from "@mui/material";
import React, { useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { componentStyle } from "../../../../config/appTheme";
import CustomSearchbarWithApiV2 from "../../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import CustomPaginationV2 from "../../../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import CustomSearchFilterV2 from "../../../../components/customComponentsV2/common/searchBar/CustomSearchFilterV2";
import CustomRadioButtonV2 from "../../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CommonBreadcrumbs from "../../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomRadioButtonWithoutForm from "../../../../components/customComponentsV2/common/radioButton/CustomRadioButtonWithoutForm";
import {
  checkedIconStyle,
  unCheckedIconStyle,
} from "../../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import AISuggestedBadge from "../../../../components/customComponentsV2/SmartMatchingDialogContainer/AISuggestedBadge";
import CustomToolTip from "../../../../components/infoButton/CustomToolTip";

function SelectLegalRequirementCard({
  displayPage,
  displayPageMap,
  lsData,
  lcData,
  getChangedLsItem,
  lsApiAction,
  searchKey,
  placeholder,
  getDataOnSearch = () => { },
  getChangedLcItem = () => { },
  lsParams,
  setLegalStandardPage,
  setLegalChapterPage,
  legalStandardPage,
  setLegalStandardRowsPerPage,
  legalStandardRowsPerPage,
  legalStandardCount,
  legalRequirementsParams,
  legalRequirementApi,
  getLrDataOnSearch,
  lrSearchKey,
  lrPlaceholder,
  lrPage,
  lrRowsPerPage,
  setLrPage,
  totalLrCount,
  legalRequirementList,
  getSelectedLrItem,
  componentType,
  setComponentType,
  savedLinkages,
  disableLrRadioButton,
  selectedLegal,
  legalChapterCount,
  legalChapterPage,
  legalChapterRowsPerPage,
  setLegalChapterCount,
  disabledLegalRequirement,
  shouldShowLegalRequirementLinkedInfo = () => false,
  selectedLegalStandard,
  selectedLegalChapter,
  aiSuggestedLegalIds = new Set(),
}) {
  const [filteredLSChapter, setFilteredLSChapter] = useState([]);
  return (
    <Box>
      {displayPageMap.ls === displayPage && (
        <Grid container>
          <Grid item sm={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: -1,
              }}
            >
              <CustomRadioButtonV2
                value={componentType}
                radioColor={"#000"}
                displayDirection="row"
                spacing={1}
                onChange={(e) => {
                  setComponentType(e.target.value);
                }}
                data={[
                  {
                    value: "all",
                    label: "All",
                  },
                  {
                    value: 3,
                    label: "VS",
                  },
                  {
                    value: 4,
                    label: "VC",
                  },
                  {
                    value: 2,
                    label: "EE",
                  },
                  {
                    value: 1,
                    label: "SWM",
                  },
                ]}
              />
            </Box>
          </Grid>
          <Grid item sm={12}>
            <CustomSearchbarWithApiV2
              apiAction={lsApiAction}
              searchKey={searchKey}
              placeholder={placeholder}
              getDataOnSearch={getDataOnSearch}
              params={lsParams}
            />
          </Grid>
          <Grid
            item
            sm={12}
            component={Paper}
            elevation={2}
            sx={{ borderRadius: 0 }}
          >
            <Box
              sx={{
                width: "100%",
                height: "255px",
                overflow: "auto",
                borderRadius: 0,
              }}
            >
              {lsData.map((item, index) => (
                <Grid
                  container
                  key={index}
                  sx={{
                    ":hover": {
                      backgroundColor: "#D5D5D5",
                      cursor: "pointer",
                    },

                    backgroundColor:
                      item.ls_id === selectedLegalStandard?.ls_id
                        ? "#D5D5D5"
                        : "#ffffff",
                  }}
                  onClick={() => {
                    getChangedLsItem(item);
                  }}
                >
                  <Grid
                    item
                    sm={11}
                    key={index}
                    sx={{
                      alignItems: "center",
                      p: 1,
                    }}
                  >
                    <Typography sx={{ ...componentStyle.font.text }}>
                      <span sx={{ fontWeight: 600 }}>Legal Standard: </span>
                      <span>{(item.standard_number || "-") + ", "}</span>
                      <span sx={{ fontWeight: 600 }}>Revision: </span>
                      <span>{item.revision || "-"}</span>
                    </Typography>
                  </Grid>
                  <Grid item sm={1}>
                    <ChevronRightIcon />
                  </Grid>
                </Grid>
              ))}
            </Box>
            <CustomPaginationV2
              size="small"
              page={legalStandardPage}
              totalItems={legalStandardCount}
              onPageChange={(page) => setLegalStandardPage(page)}
              rowsPerPage={legalStandardRowsPerPage}
            />
          </Grid>
        </Grid>
      )}

      {displayPageMap.lc === displayPage && (
        <Grid container>
          <Grid item sm={12}>
            <CustomSearchFilterV2
              placeholder={"Search Chapter"}
              searchKeys={["chapter"]}
              inputData={lcData}
              getDataOnSearch={(filteredData) => {
                setFilteredLSChapter(filteredData);
                setLegalChapterCount(filteredData.length);
              }}
            />
          </Grid>
          <Grid
            item
            sm={12}
            component={Paper}
            elevation={2}
            sx={{ borderRadius: 0 }}
          >
            <Box
              sx={{
                width: "100%",
                height: "268px",
                overflow: "auto",
              }}
            >
              {filteredLSChapter.map((item, index) => (
                <Grid
                  container
                  key={index}
                  sx={{
                    ":hover": {
                      backgroundColor: "#D5D5D5",
                      cursor: "pointer",
                    },

                    backgroundColor:
                      item.chapter === selectedLegalChapter
                        ? "#D5D5D5"
                        : "#ffffff",
                  }}
                  onClick={() => {
                    getChangedLcItem(item);
                  }}
                >
                  <Grid
                    item
                    sm={11}
                    key={index}
                    sx={{
                      alignItems: "center",
                      p: 1,
                    }}
                  >
                    <Typography sx={{ ...componentStyle.font.text }}>
                      <span sx={{ fontWeight: 600 }}>Chapter: </span>
                      <span>{item.chapter || "-"}</span>
                    </Typography>
                  </Grid>
                  <Grid item sm={1}>
                    <ChevronRightIcon />
                  </Grid>
                </Grid>
              ))}
            </Box>

            <CustomPaginationV2
              size="small"
              page={legalChapterPage}
              totalItems={legalChapterCount}
              onPageChange={(page) => setLegalChapterPage(page)}
              rowsPerPage={legalChapterRowsPerPage}
            />
          </Grid>
        </Grid>
      )}

      {displayPageMap.lr === displayPage && (
        <Grid container>
          <Grid item sm={12}>
            <CustomSearchbarWithApiV2
              apiAction={legalRequirementApi}
              searchKey={lrSearchKey}
              placeholder={lrPlaceholder}
              getDataOnSearch={getLrDataOnSearch}
              params={legalRequirementsParams}
            />
          </Grid>
          <Grid
            item
            sm={12}
            component={Paper}
            elevation={2}
            sx={{ borderRadius: 0 }}
          >
            <Box sx={{ width: "100%", height: "268px", overflow: "auto" }}>
              {legalRequirementList.map((item, index) => {
                const isLegalRequirementDisabled =
                  disabledLegalRequirement.includes(Number(item.lr_id));
                const showLinkedInfo =
                  !isLegalRequirementDisabled &&
                  shouldShowLegalRequirementLinkedInfo(item.lr_id);

                return (
                  <Grid
                    container
                    key={index}
                    sx={{
                      ":hover": {
                        backgroundColor: "#D5D5D5",
                        cursor: "pointer",
                      },

                      backgroundColor:
                        item.position === selectedLegalChapter
                          ? "#D5D5D5"
                          : "#ffffff",
                    }}
                  >
                    <Grid item sm={1}>
                      <Box sx={{ pl: 1, pt: 1 }}>
                        <Radio
                          name="lr"
                          disabled={isLegalRequirementDisabled}
                          value={item.lr_id}
                          checked={selectedLegal?.lr_id === item.lr_id}
                          onChange={(e) => {
                            e.stopPropagation();
                            getSelectedLrItem(item);
                          }}
                          icon={unCheckedIconStyle({
                            disabled: isLegalRequirementDisabled,
                          })}
                          checkedIcon={checkedIconStyle({
                            disabled: isLegalRequirementDisabled,
                          })}
                          sx={{
                            p: 0,
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      sm={11}
                      key={index}
                      sx={{
                        alignItems: "center",
                        p: 1,
                      }}
                    >
                      <Box>
                        <Box>
                          {" "}
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              fontWeight: 600,
                              ...(isLegalRequirementDisabled && {
                                color: "#9E9E9E",
                              }),
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span sx={{ fontWeight: 600 }}>
                              Legal Requirement Number:{" "}
                            </span>
                            <span>{item.req_number || "-"}</span>
                            {/* ADD ONLY — AI Suggested Badge */}
                            {aiSuggestedLegalIds.has(Number(item.lr_id)) && (
                              <AISuggestedBadge visible={true} />
                            )}
                            {showLinkedInfo && (
                              <CustomToolTip
                                type="component"
                                title={
                                  "Some selected vehicle systems are already associated with this legal requirement. These vehicle systems will be excluded when creating the ticket"
                                }
                              >
                                <IconButton
                                  size="small"
                                  sx={{ color: "black" }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ErrorOutlineIcon sx={{ fontSize: "18px" }} />
                                </IconButton>
                              </CustomToolTip>
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          {" "}
                          <CustomToolTip
                            length={100}
                            style={{
                              ...componentStyle.font.text,
                              ...(isLegalRequirementDisabled && {
                                color: "#9E9E9E",
                              }),
                            }}
                          >
                            {`Description: ${item.description || "-"}`}
                          </CustomToolTip>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
            <CustomPaginationV2
              size="small"
              page={lrPage}
              totalItems={totalLrCount}
              onPageChange={(page) => setLrPage(page)}
              rowsPerPage={legalStandardRowsPerPage}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default SelectLegalRequirementCard;